/**
 * Editor for ONE timeline event, in a modal. Owner-agnostic — a manufacturer's
 * history and a brand's are the same shape and the same rules.
 *
 * Shape borrowed from `CalibreTiersEditor`'s `TierFormModal` — a compact list of
 * rows, each opening a full editor in a modal, rather than a column of inline
 * forms. PERSISTENCE deliberately does NOT follow it: that component states in
 * its own docstring that it "never writes to Firestore itself", leaving the
 * array to the calibre modal's save. Copying that here would rebuild the exact
 * bug this rework exists to remove — neither owner's form draft carries
 * `history`, so a timeline left in form state is one the top-level Save
 * silently drops.
 *
 * The image slot is the shared {@link TimelineImageSlot}, which the history
 * hero uses too. It reports changes rather than acting on them, so the byte
 * deletion below stays here, where the draft-not-yet-written lifecycle makes an
 * immediate delete correct.
 *
 * So this modal is transactional from the curator's point of view: Save writes.
 * It hands the finished event back and the section performs the write, keeping
 * ONE writer for the array rather than two racing copies of it.
 */

import { useEffect, useState } from 'react';
import type { TimelineEvent } from '@watchkeeper/shared-types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { RichTextEditor } from '../common/RichTextEditor';
import { deleteCalibreImage } from '../../services/firebase';
import { TIMELINE_CATEGORIES, emptyTimelineEvent } from '../../utils/timeline';
import { TimelineImageSlot } from './TimelineImageSlot';

interface Props {
  isOpen: boolean;
  /**
   * Where this owner's timeline images live, e.g. `brands/rolex/timeline/`.
   * Used ONLY for the delete-safety check: bytes are removed on "Remove" just
   * when the path sits under this prefix, so a pasted foreign URL — or a path
   * belonging to another document — is unlinked and never deleted.
   */
  storagePrefix: string;
  /** Uploads bytes for this owner and returns the storage path. */
  uploadImage: (file: File | Blob, fileName?: string) => Promise<string>;
  /** The event being edited, or null to create one. */
  initial: TimelineEvent | null;
  onCancel: () => void;
  /** Persist. Rejecting keeps the modal open with the draft intact. */
  onSave: (event: TimelineEvent) => Promise<void>;
}

export function TimelineEventModal({
  isOpen,
  storagePrefix,
  uploadImage,
  initial,
  onCancel,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<TimelineEvent>(() => initial ?? emptyTimelineEvent());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Re-seed each time the modal opens on a different event.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(initial ?? emptyTimelineEvent());
  }, [isOpen, initial]);

  const set = (patch: Partial<TimelineEvent>) => setDraft((d) => ({ ...d, ...patch }));

  /**
   * Take the slot's new image into the draft.
   *
   * On a removal, drop the bytes too — but only when THIS owner owns them, so a
   * pasted foreign URL, or a path belonging to another document, is unlinked and
   * never deleted. Deleting immediately is right HERE and wrong for the hero:
   * this draft is not written yet, so there is no document left pointing at the
   * file. The hero writes on change and so must persist first.
   */
  const handleImageChange = async (next: TimelineEvent['image']) => {
    const previousPath = draft.image?.storagePath;
    set({ image: next });
    if (next === undefined && previousPath?.startsWith(storagePrefix)) {
      try {
        await deleteCalibreImage(previousPath);
      } catch (err) {
        console.warn('Timeline image delete failed (non-fatal):', err);
      }
    }
  };

  const canSave = draft.date.trim() !== '' && draft.title.trim() !== '' && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(draft);
    } catch {
      // The section reports the failure; keep the draft on screen so the work
      // is not lost to a transient write error.
    } finally {
      setSaving(false);
    }
  };

  const titleWords = draft.title.trim() ? draft.title.trim().split(/\s+/).length : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => undefined : onCancel}
      title={initial ? 'Edit timeline event' : 'Add timeline event'}
      size="xl"
      dismissOnBackdrop={false}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr_10rem] gap-3">
          <label className="block">
            <span className="block text-xs font-medium uppercase text-gray-500">Date</span>
            <input
              type="text"
              value={draft.date}
              disabled={saving}
              onChange={(e) => set({ date: e.target.value })}
              placeholder="1912"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <span className="block text-[10px] text-gray-400 mt-0.5">
              Free text — &ldquo;1930s&rdquo;, &ldquo;circa 1885&rdquo; all work.
            </span>
          </label>
          <label className="block">
            <span className="block text-xs font-medium uppercase text-gray-500">
              Title{' '}
              <span className={titleWords > 10 ? 'text-amber-600' : 'text-gray-300'}>
                (≤10 words{titleWords > 10 ? ` — currently ${titleWords}` : ''})
              </span>
            </span>
            <input
              type="text"
              value={draft.title}
              disabled={saving}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Founded in Bienne, Switzerland"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium uppercase text-gray-500">Category</span>
            <select
              value={draft.category ?? ''}
              disabled={saving}
              onChange={(e) =>
                set({ category: (e.target.value || undefined) as TimelineEvent['category'] })
              }
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value="">— none —</option>
              {TIMELINE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <span className="block text-xs font-medium uppercase text-gray-500 mb-1">
            Description{' '}
            <span className="text-gray-300">
              (rich text, 2–3 sentences — links to brands, calibres, refs)
            </span>
          </span>
          <RichTextEditor
            value={draft.description}
            onChange={(html) => set({ description: html })}
            entityLinks
            minHeight={140}
          />
        </div>

        <label className="block">
          <span className="block text-xs font-medium uppercase text-gray-500">Source URL</span>
          <input
            type="url"
            value={draft.sourceUrl ?? ''}
            disabled={saving}
            onChange={(e) => set({ sourceUrl: e.target.value })}
            placeholder="https://…"
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          />
        </label>

        <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
          <span className="block text-xs font-medium uppercase text-gray-500">Image</span>
          <TimelineImageSlot
            value={draft.image}
            onChange={(next) => void handleImageChange(next)}
            uploadImage={uploadImage}
            onUploadingChange={setUploading}
            disabled={saving}
            label="event image"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-3">
          <span className="mr-auto text-[11px] text-gray-400">
            Saving writes straight through — there is no second Save.
          </span>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            loading={saving}
            disabled={!canSave || uploading}
            title={
              canSave ? undefined : 'A date and a title are needed to place the event on a timeline'
            }
          >
            Save event
          </Button>
        </div>
      </div>
    </Modal>
  );
}
